const calculateLuminanceIndividually = (normColor: number) => {
  if (normColor <= 0.03928) {
    return normColor / 12.92;
  }

  return Math.pow((normColor + 0.055) / 1.055, 2.4);
};

const calculateLuminance = (rNorm: number, gNorm: number, bNorm: number) => {
  const linearR = calculateLuminanceIndividually(rNorm);
  const linearG = calculateLuminanceIndividually(gNorm);
  const linearB = calculateLuminanceIndividually(bNorm);

  return 0.2126 * linearR + 0.7152 * linearG + 0.0722 * linearB;
};

// This function is based on the WCAG 2.0 specification, which calculates
// the luminance on the background and finds the best best font color for readability.
// More information: https://www.w3.org/TR/WCAG20-TECHS/G18.html
const calculateLuminanceWithoutAlpha = (r: number, g: number, b: number) => {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  return calculateLuminance(rNorm, gNorm, bNorm);
};

// Normalize color taking into the account the alpha channel. This function is
// 'flattening' the color with other possible colors in the background.
const calculateColorNorm = (
  color: number,
  alpha: number,
  backgroundColor: number,
) => Math.round((1 - alpha) * backgroundColor + alpha * color) / 255;

const calculateLuminanceWithAlpha = (
  r: number,
  g: number,
  b: number,
  a: number,
) => {
  // We're always assuming it will be a white background color for now.
  const whiteBg = { r: 255, g: 255, b: 255 };

  const aNorm = a / 255;
  const rNorm = calculateColorNorm(r, aNorm, whiteBg.r);
  const gNorm = calculateColorNorm(g, aNorm, whiteBg.g);
  const bNorm = calculateColorNorm(b, aNorm, whiteBg.b);

  // At this point, all the variables should be normalized to 0~1
  // before calculating the luminance
  return calculateLuminance(rNorm, gNorm, bNorm);
};

export const getBestFontColor = (bgColorHex?: string) => {
  if (!bgColorHex) {
    return "#FFFFFF";
  }

  const hex = bgColorHex.startsWith("#") ? bgColorHex.slice(1) : bgColorHex;

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const a = parseInt(hex.substring(6, 8), 16);

  let luminance = 0;
  if (isNaN(a)) {
    luminance = calculateLuminanceWithoutAlpha(r, g, b);
  } else {
    luminance = calculateLuminanceWithAlpha(r, g, b, a);
  }

  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};
